import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ProjectContext } from "context/ProjectProvider";
import { gql, useMutation } from "@apollo/client";
import { MUTATION_SAVE_COLUMNS } from "services/graphQL/mutations";
import { getLoggedinUserId } from "services/auth";
import { AgGridReact } from "ag-grid-react";
import { message } from "utils/ciq-message";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import { MasterColumnConfigRow } from "components/column-config-views/master-column-config";
import { QUERY_MASTER_COLUMN_CONFIG } from "components/column-config-views/master-column-config-queries";
import { FeatureTypes } from "entity-app/constants";
import { getFeatureEntityLog } from "services/feature-apis";
import featureDateBlockColumns, {
  getMaxNumberOfMilestonesFromEntityList
} from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import { UnitType } from "pages/material-details/material-details-tab/units-tree";
import { QUERY_MASTER_UNITS } from "services/graphQL/queries";
import { DELETE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { TAuditChangeLog } from "change-events/change-event-polling";
import { MAX_NUMBER_OF_FETCH_ITEM } from "constants/index";
import { useStatusMilestoneNameOptions } from "hooks/use-status-milestone-name-options";
import { QUERY_GET_MATERIAL_COLUMN_CONFIG } from "../queries";
import { TMaterialLog } from "../model";

export const useMaterialLogData = (props: {
  gridRef: React.RefObject<AgGridReact<any>>;
  projectId: string;
  getConditionalsFormatting?: () => Array<any>;
}) => {
  const { gridRef, projectId, getConditionalsFormatting } = props;
  const {
    gqlClientForProject,
    tokenRetrievalState,
    columnHeaders: { materialHeaderMap },
    eventLogs,
    projectDetails,
    isProjectArchived
  } = useContext(ProjectContext);

  const effectiveProjectId = projectId || projectDetails?.id;

  const [entityList, setEntityList] = useState<Array<TMaterialLog>>();
  const [entityListLoading, setEntityListLoading] = useState(false);
  const [entityListLoadedOnce, setEntityListLoadedOnce] = useState(false);

  const fetchLogData = useCallback(async () => {
    setEntityListLoading(true);
    const res = await getFeatureEntityLog({
      projectToken: tokenRetrievalState?.token,
      featureType: FeatureTypes.MATERIALS
    });

    if (res.success) {
      setEntityList([...res.data]);
      setEntityListLoadedOnce(true);
    }
    setEntityListLoading(false);
  }, [tokenRetrievalState?.token]);

  const maxNumberOfMilestones = useMemo(() => {
    return getMaxNumberOfMilestonesFromEntityList(entityList || null);
  }, [entityList]);

  const milestonesColumns = useMemo(
    () =>
      featureDateBlockColumns(
        maxNumberOfMilestones?.milestonesCount ?? null,
        maxNumberOfMilestones?.lastMilestoneHasOffset ?? false,
        maxNumberOfMilestones?.milestoneIndicesWithOffset
      ),
    [
      maxNumberOfMilestones?.lastMilestoneHasOffset,
      maxNumberOfMilestones?.milestonesCount,
      maxNumberOfMilestones?.milestoneIndicesWithOffset
    ]
  );

  useEffect(() => {
    if (tokenRetrievalState?.token) {
      fetchLogData();
    }
    return () => {};
  }, [fetchLogData, tokenRetrievalState?.token]);

  const loggedinUserId = useMemo(() => getLoggedinUserId(), []);

  const {
    data: savedColumnsData,
    loading: userColumnConfigLoading,
    refetch: refetchUserColumnConfig
  } = useCIQQuery<{
    project_users: Array<{ material_column_config: any }>;
  }>(QUERY_GET_MATERIAL_COLUMN_CONFIG, {
    fetchPolicy: "no-cache",
    client: gqlClientForProject,
    skip: !gqlClientForProject || !loggedinUserId,
    variables: {
      where: { user_id: { _eq: loggedinUserId } }
    }
  });

  const { data: masterColumnConfig, loading: masterColumnConfigLoading } =
    useCIQQuery<{ master_column_config: MasterColumnConfigRow[] }>(
      QUERY_MASTER_COLUMN_CONFIG,
      {
        fetchPolicy: "cache-first",
        client: gqlClientForProject,
        skip: !gqlClientForProject || !effectiveProjectId,
        variables: {
          where: { project_id: { _eq: effectiveProjectId } }
        }
      }
    );

  const isColumnConfigReady =
    !userColumnConfigLoading && !masterColumnConfigLoading;

  const savedColumns = useMemo(() => {
    if (!savedColumnsData || !savedColumnsData?.project_users?.length)
      return null;

    const resArr = savedColumnsData?.project_users;
    const colConfig = resArr[resArr.length - 1];
    return colConfig;
  }, [savedColumnsData]);

  const [saveColumnsStateMutation] = useMutation<any>(MUTATION_SAVE_COLUMNS, {
    client: gqlClientForProject
  });

  const onSaveColumnState = async () => {
    if (isProjectArchived || !gridRef.current) return;
    const columnsStates = UtilColumnConfig.getColumnStateFromGrid({
      gridRef: {
        api: gridRef.current!.api
      },
      conditionals_formatting: getConditionalsFormatting?.() ?? []
    });

    try {
      const variables = {
        material_column_config: columnsStates
      };
      const updateResponse: any = await saveColumnsStateMutation({
        variables
      });
      if (updateResponse.errors) {
        message.error({ content: updateResponse.errors[0].message });
      } else {
        await refetchUserColumnConfig();
      }
    } catch {
      console.error("An error occured");
    }
  };

  const { data: allUnits } = useCIQQuery<{
    material_quantity_unit: Array<UnitType>;
    material_size_unit: Array<UnitType>;
  }>(QUERY_MASTER_UNITS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const statusMilestoneOptions = useStatusMilestoneNameOptions(
    FeatureTypes.MATERIALS
  );

  const [mutationUpdateFeaturesData] = useCIQMutation(
    gql(DELETE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const getAndUpdateEntityList = useCallback(
    async (featureInstanceIds: Array<string>) => {
      if (!entityList) return;

      if (featureInstanceIds.length >= MAX_NUMBER_OF_FETCH_ITEM) {
        fetchLogData();
        return;
      }

      const res = await getFeatureEntityLog({
        projectToken: tokenRetrievalState?.token,
        featureType: FeatureTypes.MATERIALS,
        feature_instance_ids: featureInstanceIds
      });

      if (res?.success && res.data?.length) {
        const updatedList = res.data;

        const items = [...entityList] as Array<any>;
        updatedList.forEach((item: any) => {
          const foundIndex = items.findIndex((x) => x.id === item.id);
          if (foundIndex === -1) {
            items.push(item);
          } else {
            items[foundIndex] = item;
          }
        });
        setEntityList(items);
      }
    },
    [entityList, fetchLogData, tokenRetrievalState?.token]
  );

  const deleteEntitiesFromList = useCallback(
    (ids: Array<string>) => {
      const items = entityList ? [...entityList] : [];
      ids.forEach((id) => {
        const index = items.findIndex((x) => x.id === id);
        items.splice(index, 1);
      });
      setEntityList(items);
    },
    [entityList]
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const featureLogs = eventLogs.filter(
        (log) =>
          (log.info?.feature_type === "MATERIALS" ||
            log.info?.feature_type_ids?.includes(FeatureTypes.MATERIALS)) &&
          (log.data_source === "feature_instance" ||
            log.data_source === "estimate_invite")
      );

      if (featureLogs.length) {
        const deletedIds = featureLogs
          .filter((log) => ["delete", "bulk_delete"].includes(log.change_type))
          .map((s: TAuditChangeLog) => s.info.feature_instance_ids) as any;
        if (deletedIds.length) {
          const deletedDArray = [...new Set([].concat([], ...deletedIds))];
          deleteEntitiesFromList(deletedDArray);
        }

        const ids = featureLogs
          .filter((log) => !["delete", "bulk_delete"].includes(log.change_type)) // update create import
          .map((s) => s.info.feature_instance_ids) as any;
        if (ids.length) {
          const singleDArray = [...new Set([].concat([], ...ids))];
          getAndUpdateEntityList(singleDArray);
        }
      }
    }
    previousEventLogs.current = eventLogs;
  }, [deleteEntitiesFromList, eventLogs, getAndUpdateEntityList]);

  return {
    entityList,
    entityListLoading,
    entityListLoadedOnce,
    fetchLogData,
    maxNumberOfMilestones,
    milestonesColumns,
    savedColumns,
    masterColumnConfig,
    isColumnConfigReady,
    userColumnConfigLoading,
    masterColumnConfigLoading,
    onSaveColumnState,
    materialHeaderMap,
    allUnits,
    mutationUpdateFeaturesData,
    statusMilestoneOptions
  };
};
